import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NewsService } from '../../../shared/services/news/news.service';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { UploadFilesComponent } from '../../../shared/components/upload-files/upload-files.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { finalize } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { QuillModule } from 'ngx-quill';
import { QuillConfigService } from '../../../shared/services/quill-config.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { Culture, CultureTab } from '../../../shared/interfaces/news.interface';

interface TabWithFiles {
  tabId: number;
  files: File[];
}

@Component({
  selector: 'app-culture-form',
  imports: [
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatSortModule,
    QuillModule,
    MatCheckboxModule,
    UploadFilesComponent,
    MatDatepickerModule,
    FormsModule,
    CommonModule,
    RouterLink,
    LoaderComponent,
  ],
  templateUrl: './culture-form.component.html',
  styleUrls: [
    './culture-form.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class CultureFormComponent implements OnInit {
  // Mode management
  isEditMode: boolean = false;
  cultureId: number | null = null;

  // Common properties
  modules: any;
  cultureForm: FormGroup;
  culture: Culture = {} as Culture;
  myroles: any = {};
  isSubmitting = false;

  // File handling
  tabFiles: Map<number, File[]> = new Map(); // For add mode
  selectedFiles: Map<number, File[]> = new Map(); // For edit mode
  deletedMediaTabs: Set<number> = new Set(); // Track tabs where media was deleted
  deletedTabIds: number[] = []; // Track server-side tab IDs removed by the user
  // Counters used during updateTabs to correctly track async completions
  private updatedCount = 0;
  private hasErrors = false;

  private _snackBar = inject(MatSnackBar);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private newsService: NewsService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private loaderService: LoaderService,
    private quillConfigService: QuillConfigService,
    private storageService: StorageService,
    private rolesService: RolesService
  ) {
    this.modules = this.quillConfigService.getDefaultModules();
    // Make 'name' optional by default; we'll enable required only in edit mode.
    this.cultureForm = this.fb.group({
      title: ['', [Validators.required]],
      name: [''], // removed Validators.required
      tabs: this.fb.array([]),
    });
  }

  get isLoading(): boolean {
    return this.loaderService.getLoader();
  }

  get tabs(): FormArray {
    return this.cultureForm.get('tabs') as FormArray;
  }

  get orderOptions(): number[] {
    return Array.from({ length: this.tabs.length }, (_, i) => i + 1);
  }

  ngOnInit() {
    // Check if we're in edit mode based on route params
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.isEditMode = true;
        this.cultureId = +params['id'];
        // Require 'name' in edit mode
        this.cultureForm.get('name')?.setValidators([Validators.required]);
        this.cultureForm.get('name')?.updateValueAndValidity();
        this.loadCultureData();
      } else {
        this.isEditMode = false;
        this.cultureId = null;
        // Clear 'name' validators for add mode (so empty 'name' won't mark form invalid)
        this.cultureForm.get('name')?.clearValidators();
        this.cultureForm.get('name')?.updateValueAndValidity();
        this.addTab(); // Add initial tab for create mode
      }
    });

    this.loadRoles();
  }

  private loadCultureData(): void {
    if (!this.cultureId) return;

    this.loaderService.showLoader();
    this.newsService.getCultureId(this.cultureId).subscribe({
      next: (data) => {
        this.culture = data;

        // Set the name for both form controls
        this.cultureForm.patchValue({
          name: this.culture.name,
          title: this.culture.name,
        });

        // Clear any existing tabs
        while (this.tabs.length !== 0) {
          this.tabs.removeAt(0);
        }

        // Add tabs from the loaded data
        if (this.culture.tabs && this.culture.tabs.length > 0) {
          this.culture.tabs.forEach((tab: CultureTab) => {
            this.tabs.push(
              this.fb.group({
                id: [tab.id],
                title: [tab.title, [Validators.required]],
                description: [tab.description, [Validators.required]],
                isHidden: [tab.is_hidden],
                order: [Number(tab.order), [Validators.required]],
              })
            );
          });
        }
        this.loaderService.hideLoader();
      },
      error: (error) => {
        this.loaderService.hideLoader();
        this.snackBar.open('Error al cargar los datos', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  openSnackBar(message: string) {
    const action = 'Cerrar';
    this._snackBar.open(message, action, {
      duration: 5000,
    });
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'stories',
      this.storageService
    );

    if (permissions) {
      this.myroles = permissions;

      const permissionMessage = this.isEditMode
        ? 'No tienes permisos para editar una historia'
        : 'No tienes permisos para añadir una historia';

      this.rolesService.checkWritePermission(
        'stories',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/culture/stories',
        permissionMessage
      );
    } else {
      // console.log('No user roles found in storage');
    }
  }

  addTab(): void {
    if (this.isEditMode) {
      const newOrder =
        this.tabs.length > 0
          ? Math.max(
              ...this.tabs.controls.map((c) =>
                Number(c.get('order')?.value || 0)
              )
            ) + 1
          : 1;

      const newTab = this.fb.group({
        id: [null],
        title: ['', [Validators.required]],
        description: [''],
        isHidden: [false],
        order: [newOrder, [Validators.required]],
      });

      this.tabs.push(newTab);
    } else {
      // Add mode - simpler structure
      const newTab = this.fb.group({
        title: ['', [Validators.required]],
        description: [''],
        isHidden: [false],
      });

      this.tabs.push(newTab);
    }
    this.snackBar.open('Pestaña añadida', 'Cerrar', { duration: 3000 });
  }

  removeTab(control: AbstractControl): void {
    const index = this.tabs.controls.indexOf(control);
    if (index >= 0) {
      const tabValue = control.value;
      const deletedOrder = Number(tabValue.order);
      // If the tab exists on the server, queue a DELETE call on save
      if (tabValue.id) {
        this.deletedTabIds.push(tabValue.id);
      }
      this.tabs.removeAt(index);
      this.snackBar.open('Pestaña eliminada', 'Cerrar', { duration: 3000 });
      if (this.isEditMode) {
        // Keep culture.tabs in sync so media display stays aligned with form indices
        if (this.culture.tabs) {
          this.culture.tabs.splice(index, 1);
        }
        // Re-key selectedFiles and deletedMediaTabs after the removed index
        const newSelectedFiles = new Map<number, File[]>();
        this.selectedFiles.forEach((files, key) => {
          if (key < index) newSelectedFiles.set(key, files);
          else if (key > index) newSelectedFiles.set(key - 1, files);
        });
        this.selectedFiles = newSelectedFiles;
        const newDeletedMediaTabs = new Set<number>();
        this.deletedMediaTabs.forEach((key) => {
          if (key < index) newDeletedMediaTabs.add(key);
          else if (key > index) newDeletedMediaTabs.add(key - 1);
        });
        this.deletedMediaTabs = newDeletedMediaTabs;
        // Decrement order of all tabs that came after the deleted one
        this.tabs.controls.forEach((ctrl) => {
          const current = Number(ctrl.get('order')?.value);
          if (current > deletedOrder) {
            ctrl.get('order')?.setValue(current - 1, { emitEvent: false });
          }
        });
      } else {
        // Re-key tabFiles after the removed index
        const newTabFiles = new Map<number, File[]>();
        this.tabFiles.forEach((files, key) => {
          if (key < index) newTabFiles.set(key, files);
          else if (key > index) newTabFiles.set(key - 1, files);
        });
        this.tabFiles = newTabFiles;
      }
    }
  }

  onFilesSelected(index: number, files: File[]): void {
    if (this.isEditMode) {
      this.selectedFiles.set(index, files);
      // console.log(`Files selected for tab ${index}:`, files);
    } else {
      this.tabFiles.set(index, files);
    }
  }

  // Method to delete media for a tab (edit mode only)
  deleteTabMedia(tabIndex: number): void {
    if (this.culture.tabs && this.culture.tabs[tabIndex]) {
      // Mark this tab as having deleted media
      this.deletedMediaTabs.add(tabIndex);

      // Remove media from the culture object (for UI purposes)
      this.culture.tabs[tabIndex].media = [];

      // Show snackbar confirmation
      this.snackBar.open(
        'Archivos eliminados. Guarde cambios para confirmar.',
        'Cerrar',
        {
          duration: 3000,
        }
      );
    }
  }

  saveChanges(): void {
    this.loaderService.showLoader();

    if (this.isSubmitting) {
      this.loaderService.hideLoader();
      return;
    }

    if (this.isEditMode) {
      this.saveEditMode();
    } else {
      this.saveAddMode();
    }
  }

  private saveAddMode(): void {
    // Check if any tab has files
    let hasFiles = false;
    this.tabFiles.forEach((files) => {
      if (files && files.length > 0) {
        hasFiles = true;
      }
    });

    if (!hasFiles) {
      this.loaderService.hideLoader();
      this.snackBar.open(
        'Debe subir al menos una imagen con relación de aspecto 9:16',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    // Mark all fields as touched to trigger validation
    this.cultureForm.get('title')?.markAsTouched();
    this.tabs.controls.forEach((tabControl) => {
      tabControl.get('title')?.markAsTouched();
      tabControl.get('description')?.markAsTouched();
    });

    if (this.cultureForm.invalid) {
      this.loaderService.hideLoader();
      this.snackBar.open(
        'Por favor complete todos los campos requeridos',
        'Cerrar',
        {
          duration: 3000,
        }
      );
      return;
    }

    this.isSubmitting = true;

    const cultureData = {
      name: this.cultureForm.get('title')?.value,
    };

    this.newsService
      .postCulture(cultureData)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response) => {
          const cultureId = response.id;
          this.saveTabs(cultureId);
        },
        error: (error) => {
          this.loaderService.hideLoader();
          this.snackBar.open(
            'Error al guardar la cultura empresarial',
            'Cerrar',
            { duration: 3000 }
          );
        },
      });
  }

  private saveEditMode(): void {
    // Check for duplicate order values
    const orderValues = this.tabs.controls.map((tab) =>
      Number(tab.get('order')?.value)
    );
    const hasDuplicates = orderValues.some(
      (val, idx) => orderValues.indexOf(val) !== idx
    );

    if (hasDuplicates) {
      this.loaderService.hideLoader();
      this.snackBar.open(
        'Cada pestaña debe tener un valor de orden único.',
        'Cerrar',
        { duration: 4000 }
      );
      return;
    }

    // Check if any tab has deleted media and needs new files
    let needsNewFiles = false;
    this.deletedMediaTabs.forEach((tabIndex) => {
      if (
        !this.selectedFiles.has(tabIndex) ||
        this.selectedFiles.get(tabIndex)?.length === 0
      ) {
        needsNewFiles = true;
      }
    });

    // Check if we have at least one file (either existing or new)
    let hasFiles = false;

    // Check existing files that aren't being deleted
    if (this.culture.tabs) {
      this.culture.tabs.forEach((tab: CultureTab, index: number) => {
        if (
          !this.deletedMediaTabs.has(index) &&
          tab.media &&
          tab.media.length > 0
        ) {
          hasFiles = true;
        }
      });
    }

    // Check new files being uploaded
    this.selectedFiles.forEach((files) => {
      if (files && files.length > 0) {
        hasFiles = true;
      }
    });

    if (!hasFiles) {
      this.loaderService.hideLoader();
      this.snackBar.open(
        'Debe tener al menos una imagen con relación de aspecto 9:16',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    if (needsNewFiles) {
      this.loaderService.hideLoader();
      this.snackBar.open(
        'Debe subir nuevas imágenes para las pestañas donde eliminó los archivos',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    // Mark all fields as touched to trigger validation
    this.cultureForm.get('name')?.markAsTouched();
    this.tabs.controls.forEach((tabControl) => {
      tabControl.get('title')?.markAsTouched();
      tabControl.get('description')?.markAsTouched();
      tabControl.get('order')?.markAsTouched();
    });

    if (!this.cultureForm.valid) {
      this.loaderService.hideLoader();
      this.snackBar.open(
        'Por favor completa todos los campos requeridos',
        'Cerrar',
        { duration: 3000 }
      );
      return;
    }

    this.isSubmitting = true;

    // Update the name of the culture
    const nameUpdate = {
      name: this.cultureForm.get('name')?.value,
    };

    // First update the culture name
    this.newsService
      .updateCulture(this.cultureId!, nameUpdate)
      .pipe(finalize(() => {}))
      .subscribe({
        next: () => {
          this.updateTabs();
        },
        error: (error) => {
          this.loaderService.hideLoader();
          this.snackBar.open('Error al actualizar el nombre', 'Cerrar', {
            duration: 3000,
          });
          this.isSubmitting = false;
        },
      });
  }

  // Add mode - save tabs after culture creation
  private saveTabs(cultureId: number): void {
    const tabsValue = this.tabs.value;

    if (tabsValue.length === 0) {
      this.router.navigate(['/home/culture/stories']);
      return;
    }

    let savedTabsWithFiles: TabWithFiles[] = [];
    let savedCount = 0;
    let totalCount = tabsValue.length;

    tabsValue.forEach((tab: any, index: number) => {
      const tabData = {
        title: tab.title,
        description: tab.description,
        is_hidden: tab.isHidden,
        order: index + 1,
      };

      this.newsService.postCultureTab(tabData, cultureId).subscribe({
        next: (response) => {
          savedCount++;

          const files = this.tabFiles.get(index);
          if (files && files.length > 0) {
            savedTabsWithFiles.push({
              tabId: response.id,
              files: files,
            });
          }

          if (savedCount === totalCount) {
            if (savedTabsWithFiles.length > 0) {
              this.uploadTabFiles(cultureId, savedTabsWithFiles);
            } else {
              this.snackBar.open(
                'Cultura empresarial guardada correctamente',
                'Cerrar',
                { duration: 3000 }
              );
              this.loaderService.hideLoader();
              this.router.navigate(['/home/culture/stories']);
            }
          }
        },
        error: (error) => {
          this.loaderService.hideLoader();
          savedCount++;
          if (savedCount === totalCount) {
            this.snackBar.open(
              'Algunos tabs no se pudieron guardar correctamente',
              'Cerrar',
              { duration: 3000 }
            );
            this.router.navigate(['/home/culture/stories']);
          }
        },
      });
    });
  }

  // Edit mode - update existing tabs
  private updateTabs(): void {
    const tabsToUpdate = this.tabs.controls.length + this.deletedTabIds.length;
    this.updatedCount = 0;
    this.hasErrors = false;

    if (tabsToUpdate === 0) {
      this.loaderService.hideLoader();
      this.snackBar.open('No hay pestañas para actualizar', 'Cerrar', {
        duration: 3000,
      });
      this.router.navigate(['/home/culture/stories']);
      return;
    }

    // DELETE removed tabs
    this.deletedTabIds.forEach((tabId) => {
      this.newsService.deleteCultureTab(this.cultureId!, tabId).subscribe({
        next: () => {
          this.updatedCount++;
          this.checkCompletion(this.updatedCount, tabsToUpdate, this.hasErrors);
        },
        error: (error) => {
          this.hasErrors = true;
          this.updatedCount++;
          this.checkCompletion(this.updatedCount, tabsToUpdate, this.hasErrors);
        },
      });
    });

    this.tabs.controls.forEach((tabControl, index) => {
      const tabValue = tabControl.value;
      const tabId = tabValue.id;

      const tabData = {
        title: tabValue.title,
        description: tabValue.description,
        is_hidden: tabValue.isHidden,
        order: Number(tabValue.order) || index + 1,
      };

      // For existing tabs, update them
      if (tabId) {
        this.newsService
          .updateCultureTab(this.cultureId!, tabId, tabData)
          .subscribe({
            next: () => {
              if (this.deletedMediaTabs.has(index)) {
                this.handleDeletedMedia(tabId, index, tabsToUpdate);
              } else if (
                this.selectedFiles.has(index) &&
                this.selectedFiles.get(index)?.length
              ) {
                this.uploadFiles(tabId, index, tabsToUpdate);
              } else {
                this.updatedCount++;
                this.checkCompletion(
                  this.updatedCount,
                  tabsToUpdate,
                  this.hasErrors
                );
              }
            },
            error: (error) => {
              this.loaderService.hideLoader();
              this.hasErrors = true;
              this.updatedCount++;
              this.checkCompletion(
                this.updatedCount,
                tabsToUpdate,
                this.hasErrors
              );
            },
          });
      } else {
        // For new tabs, create them
        this.newsService.postCultureTab(tabData, this.cultureId!).subscribe({
          next: (response) => {
            const newTabId = response.id;
            if (
              this.selectedFiles.has(index) &&
              this.selectedFiles.get(index)?.length
            ) {
              this.uploadFiles(newTabId, index, tabsToUpdate);
            } else {
              this.updatedCount++;
              this.checkCompletion(
                this.updatedCount,
                tabsToUpdate,
                this.hasErrors
              );
            }
          },
          error: (error) => {
            this.hasErrors = true;
            this.updatedCount++;
            this.checkCompletion(
              this.updatedCount,
              tabsToUpdate,
              this.hasErrors
            );
          },
        });
      }
    });
  }

  // Upload files for all tabs (add mode)
  private uploadTabFiles(
    cultureId: number,
    tabsWithFiles: TabWithFiles[]
  ): void {
    let totalUploads = tabsWithFiles.reduce(
      (total, tab) => total + tab.files.length,
      0
    );
    let completedUploads = 0;

    tabsWithFiles.forEach((tab) => {
      tab.files.forEach((file) => {
        const formData = new FormData();
        formData.append('file', file);

        this.newsService
          .postCultureTabMedia(tab.tabId, cultureId, formData)
          .subscribe({
            next: () => {
              completedUploads++;
              if (completedUploads === totalUploads) {
                this.snackBar.open(
                  'Cultura empresarial guardada correctamente',
                  'Cerrar',
                  { duration: 3000 }
                );
                this.loaderService.hideLoader();
                this.router.navigate(['/home/culture/stories']);
              }
            },
            error: (error) => {
              completedUploads++;
              if (completedUploads === totalUploads) {
                this.snackBar.open(
                  'Algunos archivos no se pudieron subir correctamente',
                  'Cerrar',
                  { duration: 3000 }
                );
                this.loaderService.hideLoader();
                this.router.navigate(['/home/culture/stories']);
              }
            },
          });
      });
    });

    if (totalUploads === 0) {
      this.loaderService.hideLoader();
      this.router.navigate(['/home/culture/stories']);
    }
  }

  // Handle deleted media by removing all media for the tab (edit mode)
  private handleDeletedMedia(
    tabId: number,
    tabIndex: number,
    tabsToUpdate: number
  ): void {
    if (
      this.culture.tabs[tabIndex] &&
      this.culture.tabs[tabIndex].media &&
      this.culture.tabs[tabIndex].media.length > 0
    ) {
      const mediaToDelete = this.culture.tabs[tabIndex].media.length;
      let deletedCount = 0;

      this.culture.tabs[tabIndex].media.forEach((media: any) => {
        deletedCount++;

        if (deletedCount === mediaToDelete) {
          if (
            this.selectedFiles.has(tabIndex) &&
            this.selectedFiles.get(tabIndex)?.length
          ) {
            this.uploadFiles(tabId, tabIndex, tabsToUpdate);
          } else {
            this.updatedCount++;
            this.checkCompletion(
              this.updatedCount,
              tabsToUpdate,
              this.hasErrors
            );
          }
        }
      });
    } else {
      if (
        this.selectedFiles.has(tabIndex) &&
        this.selectedFiles.get(tabIndex)?.length
      ) {
        this.uploadFiles(tabId, tabIndex, tabsToUpdate);
      } else {
        this.updatedCount++;
        this.checkCompletion(this.updatedCount, tabsToUpdate, this.hasErrors);
      }
    }
  }

  // Upload files for a tab (edit mode)
  private uploadFiles(
    tabId: number,
    tabIndex: number,
    tabsToUpdate: number
  ): void {
    const files = this.selectedFiles.get(tabIndex) || [];
    if (files.length === 0) {
      this.updatedCount++;
      this.checkCompletion(this.updatedCount, tabsToUpdate, this.hasErrors);
      return;
    }

    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append('file', files[i]);
    }

    this.newsService
      .postCultureTabMedia(tabId, this.cultureId!, formData)
      .subscribe({
        next: (response) => {
          this.updatedCount++;
          this.checkCompletion(this.updatedCount, tabsToUpdate, this.hasErrors);
        },
        error: (error) => {
          this.hasErrors = true;
          this.updatedCount++;
          this.checkCompletion(this.updatedCount, tabsToUpdate, this.hasErrors);
        },
      });
  }

  private checkCompletion(
    current: number,
    total: number,
    hasErrors: boolean
  ): void {
    if (current === total) {
      this.loaderService.hideLoader();
      if (hasErrors) {
        this.snackBar.open('Algunos cambios no se pudieron guardar', 'Cerrar', {
          duration: 3000,
        });
      } else {
        this.snackBar.open('Cambios guardados correctamente', 'Cerrar', {
          duration: 3000,
        });
      }
      this.router.navigate(['/home/culture/stories']);
    }
  }

  cancel(): void {
    this.loaderService.hideLoader();
    this.router.navigate(['/home/culture/stories']);
  }
}
